import type { TripEventSource, TripEventType } from '../validation/trip-event.schemas';

/**
 * The trip lifecycle (PRD → Trip lifecycle; gitaumoses4/chukua#44). A trip has no state until it is dispatched, so
 * `null` stands for "nothing yet" and the trip row calls it `pending`. `aborted` and `ready_to_invoice` are terminal.
 * `aborted` is reachable from any live state before delivery (gitaumoses4/chukua#56): once the goods are handed over the trip is finished, not failed.
 */
export const TRIP_STATES = ['dispatched', 'pre_trip_failed', 'in_transit', 'exception', 'delivered', 'aborted', 'close_out', 'ready_to_invoice'] as const;
export type TripState = (typeof TRIP_STATES)[number];

/** The event types that can move a trip. Every other type is a fact about the trip that leaves its state alone. */
export type StateChangingEventType = Extract<
  TripEventType,
  'dispatched' | 'pre_trip_failed' | 'departed' | 'exception' | 'exception_cleared' | 'aborted' | 'pod_captured' | 'closed_out' | 'ready_to_invoice'
>;

/** The one statement of the arcs: (state the trip is in, event) → state it moves to. Anything not listed is refused. */
export const TRIP_TRANSITIONS: Record<TripState | 'none', Partial<Record<StateChangingEventType, TripState>>> = {
  // An assigned trip has no events yet, so closing it out straight away (gitaumoses4/chukua#322) starts from `none`.
  none: { dispatched: 'dispatched', closed_out: 'close_out' },
  dispatched: { pre_trip_failed: 'pre_trip_failed', departed: 'in_transit', aborted: 'aborted' },
  pre_trip_failed: { dispatched: 'dispatched', aborted: 'aborted' },
  in_transit: { exception: 'exception', pod_captured: 'delivered', aborted: 'aborted' },
  exception: { exception_cleared: 'in_transit', aborted: 'aborted' },
  delivered: { closed_out: 'close_out' },
  close_out: { ready_to_invoice: 'ready_to_invoice' },
  aborted: {},
  ready_to_invoice: {},
};

/**
 * Who may write each state-changing event. A GPS feed can say a truck has an exception but never that it delivered:
 * proof of delivery is the driver's or the office's. Closing out and aborting are staff decisions.
 */
export const TRIP_EVENT_ALLOWED_SOURCES: Record<StateChangingEventType, readonly TripEventSource[]> = {
  dispatched: ['office', 'system'],
  pre_trip_failed: ['driver_app', 'office'],
  departed: ['driver_app', 'office'],
  exception: ['driver_app', 'office', 'gps', 'system'],
  exception_cleared: ['driver_app', 'office'],
  pod_captured: ['driver_app', 'office'],
  aborted: ['office'],
  closed_out: ['office'],
  ready_to_invoice: ['system', 'office'],
};

/** Recorded before a trip is dispatched too: the driver's phone may confirm or accept ahead of the office's own record. */
export const PRE_DISPATCH_EVENT_TYPES: readonly TripEventType[] = ['trip_opened', 'accepted', 'driver_confirmed'];

export function isStateChangingEvent(type: TripEventType): type is StateChangingEventType {
  return type in TRIP_EVENT_ALLOWED_SOURCES;
}
